import React, {useMemo} from "react";
import {DateTime} from "luxon";
import {getCalendarYearGridDates} from "../../helpers/calendar/helpers";
import {getHeatmapIntensity} from "./helpers";
import type {CalendarYearHeatmapProps} from "./types";
import "./calendar-year-heatmap.scss";

const WEEKDAY_LABELS = ['Sun', '', 'Tue', '', 'Thu', '', 'Sat'];

export default function CalendarYearHeatmap<TDay>({year, days, getActivityCount, getDayLabel, ariaLabel, onSelectDate, renderDayMarkers, legendMarkers}: CalendarYearHeatmapProps<TDay>) {
    const dates = useMemo(() => getCalendarYearGridDates(year), [year]);
    const maxActivityCount = Math.max(1, ...Object.values(days).map(getActivityCount));
    const weekCount = dates.length / 7;

    return <section className="calendar-year-heatmap" aria-label={ariaLabel ?? `${year} birding activity heatmap`}>
        <div className="calendar-year-heatmap__months" aria-hidden="true">
            {Array.from({length: 12}, (_, index) => <span key={index}>{DateTime.local(year, index + 1).toFormat('LLL')}</span>)}
        </div>
        <div className="calendar-year-heatmap__body">
            <div className="calendar-year-heatmap__weekdays" aria-hidden="true">
                {WEEKDAY_LABELS.map((label, index) => <span key={index}>{label}</span>)}
            </div>
            <div className="calendar-year-heatmap__grid" style={{'--calendar-year-week-count': weekCount} as React.CSSProperties}>
                {dates.map(date => {
                    const dateTime = DateTime.fromISO(date);
                    const day = days[date];
                    const isCurrentYear = dateTime.year === year;
                    const intensity = day ? getHeatmapIntensity(getActivityCount(day), maxActivityCount) : 0;
                    const label = getDayLabel(date, day);

                    return <button
                        key={date}
                        type="button"
                        disabled={!isCurrentYear}
                        className={`calendar-year-heatmap__day ${day ? 'has-activity' : ''} ${onSelectDate && isCurrentYear ? 'is-interactive' : ''}`}
                        style={day ? ({'--heatmap-intensity': intensity} as React.CSSProperties) : undefined}
                        aria-label={label}
                        onClick={onSelectDate ? () => onSelectDate(date) : undefined}
                    >
                        {day ? renderDayMarkers?.(day) : null}
                    </button>;
                })}
            </div>
        </div>
        <div className="calendar-year-heatmap__legend">
            <span>Less activity</span><i /><i /><i /><i /><span>More activity</span>
            {legendMarkers}
        </div>
    </section>;
}
